import { useCallback, useContext, useMemo } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { AccrualImporterContext } from "./accrualImporter.context";
import { buildAccrualImporterViewRows } from "./accrualImporter.helpers";
import { AccrualImporterSort } from "./accrualImporter.types";
import {
    getAccrualDocumentImporterView,
    getAccrualDocumentImporterViewConfig,
} from "./accrualDocumentImporterViewConfig";
import { AccrualImporterTableMeta } from "./accrualDocumentImporterCells";
import { getAccrualDocumentImporterConfig } from "./accrualDocumentImporter.lib";
import styles from "./AccrualDocumentImporter.module.scss";

const rowHeight = 40;
const headerHeight = 140;

export const AccrualImporterPageContent: React.FC = () => {
    const { mode, sortedRows, sort, setSort } = useContext(
        AccrualImporterContext,
    );
    const config = useMemo(
        () => getAccrualDocumentImporterConfig(mode),
        [mode],
    );

    const emptyRowsNumber = Math.max(
        5,
        Math.floor((window.innerHeight - headerHeight) / rowHeight) -
            sortedRows.length,
    );

    const tableData = useMemo(
        () =>
            buildAccrualImporterViewRows({
                sortedRows,
                emptyRowsNumber,
            }),
        [sortedRows, emptyRowsNumber],
    );

    const view = useMemo(() => getAccrualDocumentImporterView(), []);

    const viewConfig = useMemo(
        () =>
            getAccrualDocumentImporterViewConfig(
                config.counterpartyColumnHeader,
            ),
        [config.counterpartyColumnHeader],
    );

    const handleSortChange = useCallback(
        (newSort: AccrualImporterSort) => {
            setSort(newSort);
        },
        [setSort],
    );

    const handleSortRemove = useCallback(() => {
        setSort(undefined);
    }, [setSort]);

    const tableMeta: AccrualImporterTableMeta = useMemo(
        () => ({
            callbacks: {},
        }),
        [],
    );

    return (
        <Scrollbars
            style={{ height: "calc(100% - 39px)", width: "100%" }}
            className={styles.tableScrollbars}
        >
            <ViewTable
                className={styles.table}
                data={tableData}
                view={view}
                config={viewConfig}
                tableMeta={tableMeta}
                sort={sort}
                onSortChange={handleSortChange}
                onSortRemove={handleSortRemove}
                enableRowSelection={false}
            />
        </Scrollbars>
    );
};
